import { SeaEventTypeEnum } from '../../enums/seaEventEnum';
import { SeaEventTypeInputEnum } from '../../enums/seaEventTypeInputEnum';
import { Port } from '../../types/ViewModel/Port';
import { PortCall } from '../../types/ViewModel/PortCall';
import { TankersEvent } from '../../types/ViewModel/TankersEvent';
import { TimeZone } from '../../types/ViewModel/TimeZone';
import { addDateHours, getDateAsUtc, isDateAfter, isDateBefore, isSameDate } from 'odfjell-date-time-utils/dateTimeUtils';

export const getDateWithoutSecMs = (evDate: Date | null): Date | null => {
    if (evDate) {
        evDate.setSeconds(0, 0);
        return evDate;
    }
    return null;
};

export const compareActualEventDateTime = (a: TankersEvent, b: TankersEvent, desc: boolean): number => {
    let cmp = 0;
    if (isDateAfter(a.eventDate.actualDateTimeUtc, b.eventDate.actualDateTimeUtc)) {
        cmp = 1;
    } else if (isDateBefore(a.eventDate.actualDateTimeUtc, b.eventDate.actualDateTimeUtc)) {
        cmp = -1;
    }
    return desc ? cmp * -1 : cmp;
};

export const compareActualEventDateTimeWithoutSecMs = (a: TankersEvent, b: TankersEvent, desc: boolean): number => {
    const dateA = getDateWithoutSecMs(a.eventDate.actualDateTimeUtc);
    const dateB = getDateWithoutSecMs(b.eventDate.actualDateTimeUtc);
    let cmp = 0;
    if (isDateAfter(dateA, dateB)) {
        cmp = 1;
    } else if (isDateBefore(dateA, dateB)) {
        cmp = -1;
    }
    return desc ? cmp * -1 : cmp;
};

export const createUtcTimeAdjustTimezones = (portCall: PortCall, ev: TankersEvent, localTime: Date, evArray: TankersEvent[] | null): Date => {
    let utc = getUtcTime(portCall?.port, localTime);
    // Filter out the current event to avoid circular reference when calculating timezone adjustments
    const eventsForCalculation = ev?.eventId && evArray ? evArray.filter((e) => e.eventId !== ev.eventId) : evArray;

    let adj = getAdjustedTimeZoneHours(portCall, utc, eventsForCalculation);
    const adjIDL = getAdjustedIDLCrossingHours(portCall, ev, utc, eventsForCalculation);
    adj += adjIDL;
    utc = addDateHours(utc, adj);
    return utc;
};

const getAdjustedHours = (ev: TankersEvent, evTypeInputId: SeaEventTypeInputEnum): number => {
    let adjustHours = 0;
    if (ev.eventTypeInputRegList && ev.eventTypeInputRegList.length > 0) {
        // Check for 'Number of hours' dropdown input
        const evInp = ev.eventTypeInputRegList.find((evInpReg) => evInpReg.eventTypeInputId === evTypeInputId);
        if (
            evInp &&
            evInp.dropdownGroupReg &&
            evInp.dropdownGroupReg.dropdownItemRegList &&
            evInp.dropdownGroupReg.dropdownItemRegList.length === 1
        ) {
            const hoursStr = evInp.dropdownGroupReg.dropdownItemRegList[0].dropdownItemValue;
            const hours: number = +hoursStr.substring(0, hoursStr.length - 1);
            adjustHours += hours;
        }
    }
    return adjustHours;
};

const getAdjustedTimeZoneHours = (portCall: PortCall, datetime: Date, evArray: TankersEvent[] | null): number => {
    if (!evArray) return 0;
    let adjustHours = 0;

    const evListPc = evArray.filter((ev) => ev.portCallId === portCall?.portCallId && !ev.deleted);

    const evList = evListPc.filter((ev) => {
        const date1 = getDateWithoutSecMs(datetime);
        // FIX: Use actualDateTimeUtc directly instead of getAdjustedDate to avoid circular dependency
        // The timezone event's actualDateTimeUtc is already in the ORIGINAL timezone (before the timezone change it represents)
        // So we should compare directly without applying the adjustment hours backwards
        const date2 = ev.eventDate?.actualDateTimeUtc ? getDateWithoutSecMs(ev.eventDate.actualDateTimeUtc) : null;
        const isTimeZoneEvent = ev.eventTypeId === SeaEventTypeEnum.AdjustHoursAfterTimeZoneChange;
        const shouldInclude = isTimeZoneEvent && date1 && date2 && (isSameDate(date1, date2) || isDateAfter(date1, date2));
        return shouldInclude;
    });
    evList.forEach((ev) => {
        const hours = getAdjustedHours(ev, SeaEventTypeInputEnum.AdjustHoursAfterTimeZoneChangeInput);
        adjustHours += hours;
    });
    return adjustHours;
};

const getAdjustedIDLCrossingHours = (portCall: PortCall, actualEv: TankersEvent, datetime: Date, evArray: TankersEvent[] | null): number => {
    if (!evArray) {
        // Still check the actualEv for IDL crossing adjustment even without an events array
        if (actualEv.eventTypeId === SeaEventTypeEnum.IDLCrossing) {
            return getAdjustedHours(actualEv, SeaEventTypeInputEnum.IDLCrossingInput);
        }
        return 0;
    }
    let adjustHours = 0;

    const evListPc = evArray.filter((ev) => ev.portCallId === portCall?.portCallId && !ev.deleted);
    const evList = evListPc.filter((ev) => {
        const date1 = getDateWithoutSecMs(datetime);
        // FIX: Use actualDateTimeUtc directly instead of getAdjustedDate to avoid circular dependency
        // The IDL crossing event's actualDateTimeUtc is already in the ORIGINAL timezone (before the IDL crossing it represents)
        const date2 = ev.eventDate?.actualDateTimeUtc ? getDateWithoutSecMs(ev.eventDate.actualDateTimeUtc) : null;
        if (ev.eventTypeId === SeaEventTypeEnum.IDLCrossing && date1 && date2 && (isSameDate(date1, date2) || isDateAfter(date1, date2))) return true;
        return false;
    });
    evList.forEach((ev) => {
        adjustHours += getAdjustedHours(ev, SeaEventTypeInputEnum.IDLCrossingInput);
    });
    if (actualEv.eventTypeId === SeaEventTypeEnum.IDLCrossing) {
        adjustHours += getAdjustedHours(actualEv, SeaEventTypeInputEnum.IDLCrossingInput);
    }
    return adjustHours;
};

const getUtcTime = (port: Port | null, localTime: Date): Date => {
    const offset = getImosOffset(port, localTime);
    return convertDateToUtc(localTime, offset);
};

export const getImosOffset = (port: Port | null, localTime: Date): number => {
    let offset = 0;
    if (port) {
        port.timeZoneArray.forEach((tz) => {
            if (tz.year === localTime.getFullYear()) {
                if (isDaylightSaving(tz)) {
                    if (isNorthernHemisphere(tz)) {
                        if (localTimeIsInNorthernHemisphereDst(tz, localTime)) {
                            offset = tz.utcOffsetDst;
                        } else {
                            offset = tz.utcOffset;
                        }
                    } else {
                        if (localTimeIsInSouthernHemisphereDst(tz, localTime)) {
                            // Southern dst is opposite of dst for northern Hemisphere
                            offset = tz.utcOffsetDst;
                        } else {
                            offset = tz.utcOffset;
                        }
                    }
                } else {
                    offset = tz.utcOffset;
                }
            }
        });
    }
    return offset;
};

export const convertToLocalTime = (date: Date, port: Port | null): Date => {
    const copiedDate = new Date(date.getTime());
    const machineOffset = copiedDate.getTimezoneOffset() / -60;
    const imosOffset = getImosOffset(port, copiedDate);
    const offset = imosOffset - machineOffset;
    copiedDate.setHours(copiedDate.getHours() + offset);
    const remainderInHours = (offset - Math.floor(offset)) * 60;
    copiedDate.setMinutes(copiedDate.getMinutes() + remainderInHours);
    return copiedDate;
};

const isDaylightSaving = (tz: TimeZone): boolean => {
    return tz.dstStartLocalTime != null;
};

const isNorthernHemisphere = (tz: TimeZone): boolean => {
    if (tz.dstStartLocalTime && tz.dstEndLocalTime) {
        return tz.dstStartLocalTime < tz.dstEndLocalTime;
    }
    return false;
};

const localTimeIsInNorthernHemisphereDst = (tz: TimeZone, localTime: Date): boolean => {
    if (tz.dstStartLocalTime && tz.dstEndLocalTime) {
        return localTime >= tz.dstStartLocalTime && localTime <= tz.dstEndLocalTime;
    }
    return false;
};

const localTimeIsInSouthernHemisphereDst = (tz: TimeZone, localTime: Date): boolean => {
    if (tz.dstStartLocalTime && tz.dstEndLocalTime) {
        return tz.dstStartLocalTime <= localTime || localTime <= tz.dstEndLocalTime;
    }
    return false;
};

const convertDateToUtc = (d: Date, imosUtcOffset: number) => {
    if (isNaN(imosUtcOffset)) {
        return d; // Cannot convert it if imosUtcOffset is not valid.
    }

    const date = getDateAsUtc(d);
    const h1 = Math.floor(date.getHours());
    const h2 = Math.floor(imosUtcOffset);
    const machineOffset = d.getTimezoneOffset() / -60;
    const m1 = date.getMinutes();
    const m2 = (imosUtcOffset * 60) % 60;
    date.setHours(h1 - h2 - machineOffset, m1 - m2);
    return date;
};
